/**
 * Instant Connected Product Swatching
 * Performance-friendly prefetching & instant section swapping for metafield-connected products
 */

(function () {
  const sectionCache = new Map();
  const imagePreloadCache = new Set();
  let pendingFetchUrl = null;

  function getSectionId(productInfo) {
    return productInfo ? productInfo.dataset.section : null;
  }

  function prefetchProductSection(productUrl, sectionId, featuredImage) {
    if (!productUrl || !sectionId) return;

    if (featuredImage && !imagePreloadCache.has(featuredImage)) {
      imagePreloadCache.add(featuredImage);
      const img = new Image();
      img.src = featuredImage;
    }

    if (sectionCache.has(productUrl)) return sectionCache.get(productUrl);

    const separator = productUrl.includes('?') ? '&' : '?';
    const requestUrl = `${productUrl}${separator}section_id=${sectionId}`;

    const fetchPromise = fetch(requestUrl)
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.text();
      })
      .catch((err) => {
        sectionCache.delete(productUrl);
        return null;
      });

    sectionCache.set(productUrl, fetchPromise);
    return fetchPromise;
  }

  function updateSwatchUI(clickedSwatch) {
    const container = clickedSwatch.closest('.linked-color-swatches, sibling-picker');
    if (!container) return;

    const allSwatches = container.querySelectorAll('.linked-color-swatch, .sibling-item');
    allSwatches.forEach((swatch) => {
      if (swatch === clickedSwatch) {
        swatch.classList.add('linked-color-swatch--active');
        swatch.setAttribute('data-selected', 'true');
        swatch.setAttribute('aria-current', 'true');
      } else {
        swatch.classList.remove('linked-color-swatch--active');
        swatch.setAttribute('data-selected', 'false');
        swatch.removeAttribute('aria-current');
      }
    });

    const colorName = clickedSwatch.getAttribute('data-color-name') || clickedSwatch.getAttribute('title');
    if (colorName) {
      const labelSpan = container.querySelector('.linked-color-label span, .form__label-info');
      if (labelSpan) {
        labelSpan.textContent = colorName;
      }
    }
  }

  async function handleSwatchClick(event, swatch) {
    const productUrl = swatch.getAttribute('data-product-url') || swatch.getAttribute('href');
    if (!productUrl) return;

    const currentProductInfo = document.querySelector('product-info');
    if (!currentProductInfo) return;

    const currentUrl = currentProductInfo.dataset.url || window.location.pathname;
    if (currentUrl === productUrl) {
      event.preventDefault();
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    // 1. Instant 0ms visual feedback for clicked swatch & label
    updateSwatchUI(swatch);

    const sectionId = getSectionId(currentProductInfo);
    if (!sectionId) return;

    pendingFetchUrl = productUrl;

    // 2. Fetch section HTML (uses cache if pre-fetched on hover/touch)
    const featuredImage = swatch.getAttribute('data-featured-image');
    let htmlText = await prefetchProductSection(productUrl, sectionId, featuredImage);

    // If another click occurred in the meantime, ignore
    if (pendingFetchUrl !== productUrl) return;

    if (!htmlText) {
      // Fallback to normal navigation if fetch fails
      window.location.href = productUrl;
      return;
    }

    // 3. Swap section content cleanly
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlText, 'text/html');
    const newProductInfo = doc.querySelector(`product-info[id="${currentProductInfo.id}"]`) || doc.querySelector('product-info');

    if (!newProductInfo) {
      window.location.href = productUrl;
      return;
    }

    // Update browser URL & document title
    window.history.pushState({}, '', productUrl);
    if (doc.title) {
      document.title = doc.title;
    }

    // Perform smooth transition / swap using theme's HTMLUpdateUtility if present
    if (window.HTMLUpdateUtility && typeof window.HTMLUpdateUtility.viewTransition === 'function') {
      window.HTMLUpdateUtility.viewTransition(currentProductInfo, newProductInfo, [], [
        (newNode) => {
          if (window.initProductSwiper) {
            window.initProductSwiper(newNode);
          }
          newNode.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
        }
      ]);
    } else {
      currentProductInfo.replaceWith(newProductInfo);
      if (window.initProductSwiper) {
        window.initProductSwiper(newProductInfo);
      }
      newProductInfo.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
    }
  }

  // Pre-fetch intent handlers (pointerenter on desktop, touchstart on mobile)
  document.addEventListener('pointerenter', function (event) {
    const swatch = event.target.closest('.linked-color-swatch, .sibling-item');
    if (!swatch) return;

    const productUrl = swatch.getAttribute('data-product-url') || swatch.getAttribute('href');
    const productInfo = document.querySelector('product-info');
    const sectionId = getSectionId(productInfo);
    const featuredImage = swatch.getAttribute('data-featured-image');

    prefetchProductSection(productUrl, sectionId, featuredImage);
  }, true);

  document.addEventListener('touchstart', function (event) {
    const swatch = event.target.closest('.linked-color-swatch, .sibling-item');
    if (!swatch) return;

    const productUrl = swatch.getAttribute('data-product-url') || swatch.getAttribute('href');
    const productInfo = document.querySelector('product-info');
    const sectionId = getSectionId(productInfo);
    const featuredImage = swatch.getAttribute('data-featured-image');

    prefetchProductSection(productUrl, sectionId, featuredImage);
  }, { passive: true, capture: true });

  // Delegated click listener
  document.addEventListener('click', function (event) {
    const swatch = event.target.closest('.linked-color-swatch, .sibling-item');
    if (swatch) {
      handleSwatchClick(event, swatch);
    }
  }, true);

})();
